import React from "react"
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin } from 'lucide-react';
import { useSiteSettings, safeUrl } from '../services/SiteSettings';

interface FooterProps {
  onPageChange: (page: string) => void;
}

export function Footer({ onPageChange }: FooterProps) {
  const { contact, social } = useSiteSettings();
  const socialLinks = [
    { url: safeUrl(social.facebook), label: 'Facebook', Icon: Facebook },
    { url: safeUrl(social.twitter), label: 'Twitter', Icon: Twitter },
    { url: safeUrl(social.linkedin), label: 'LinkedIn', Icon: Linkedin },
  ].filter((s) => s.url);

  const usefulLinks = [
    { name: 'Who We Are', key: 'who-we-are', href: '/who-we-are' },
    { name: 'Area of Expertise', key: 'expertise', href: '/expertise' },
    { name: 'Contact Us', key: 'contact', href: '/contact' },
    { name: 'News', key: 'news', href: '/news' },
  ];

  const quickLinks = [
    { name: 'Careers', key: 'careers', href: '/careers' },
    { name: 'News', key: 'news', href: '/news' },
    { name: 'Privacy Policy', key: 'privacy', href: '/privacy' },
    { name: 'Team', key: 'team', href: '/team' },
  ];

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, page: string) => {
    e.preventDefault();
    onPageChange(page);
  };

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Contact Info */}
          <div>
            <h3 className="mb-4">Contact Info</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <span className="text-sm">{contact.addressDisplay}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <a href={`mailto:${contact.email}`} className="text-sm hover:underline">{contact.email}</a>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">
                  {contact.phones.map((p, i) => (
                    <React.Fragment key={p}>
                      {i > 0 && ' / '}
                      <a href={`tel:${p.replace(/[^\d+]/g, '')}`} className="hover:underline">{p}</a>
                    </React.Fragment>
                  ))}
                </span>
              </div>
            </div>
          </div>


          {/* Useful Links */}
          <div>
            <h3 className="mb-4">Useful Links</h3>
            <ul className="space-y-2">
              {usefulLinks.map((link) => (
                <li key={link.key}>
                  <a
                    href={link.href}
                    onClick={(e) => handleLinkClick(e, link.key)}
                    className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.key}>
                  <a
                    href={link.href}
                    onClick={(e) => handleLinkClick(e, link.key)}
                    className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Logo and Social */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <img
                src="/usp.png"
                alt="USP Consultancy Logo"
                className="h-24 w-auto"
              />
              <span className="text-xl">USP Consultancy</span>
            </div>
            <p className="text-sm text-gray-300 mb-4">
              Your trusted partner for quality service at affordable costs.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map(({ url, label, Icon }) => (
                <a key={label} href={url} target="_blank" rel="noopener noreferrer" aria-label={`${contact.name} on ${label}`}>
                  <Icon className="w-5 h-5 text-gray-300 hover:text-white cursor-pointer transition-colors" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-300 mb-2 md:mb-0">
              © {new Date().getFullYear()} USP Consultancy. All rights reserved.
            </p>

          </div>
        </div>
      </div>
    </footer>
  );
}